package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CustomerSatisfaction
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.CustomerSatisfactionRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

private const val GOAL_PERCENT = 30.0

class RemoteCustomerSatisfactionRepository(
    private val httpClient: HttpClient,
    private val orgChartRepository: OrgChartRepository,
    private val authRepository: AuthRepository,
    private val baseUrl: String,
) : CustomerSatisfactionRepository {
    override suspend fun load(
        period: MonthPeriod,
        unitName: String,
    ): List<CustomerSatisfaction> {
        val startDate = "${period.year}-${period.month.toString().padStart(2, '0')}-01"
        val endDate = "${period.year}-${period.month.toString().padStart(2, '0')}-${daysInMonth(period)}"
        val companies =
            orgChartRepository
                .loadInitial()
                .currentVersion
                .data
                .companies
                .filter { it.name.equals(unitName, ignoreCase = true) && !it.inactive }

        if (companies.isEmpty()) return emptyList()

        val response =
            httpClient.post("$baseUrl/satisfaction/analytics") {
                contentType(ContentType.Application.Json)
                authRepository.session.value?.token?.let { token ->
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                setBody(
                    AnalyticsRequestDto(
                        startDate = startDate,
                        endDate = endDate,
                        companyIds = companies.map(Company::id),
                    ),
                )
            }
        if (!response.status.isSuccess()) {
            error("Portal satisfaction analytics returned HTTP ${response.status.value}: ${response.bodyAsText()}")
        }

        val analytics = response.body<AnalyticsResponseDto>().companies
        return companies.flatMap { company ->
            analytics[company.id]?.votesByDay?.map { day -> day.toDomain(company) }.orEmpty()
        }
    }

    private fun daysInMonth(period: MonthPeriod): String =
        when (period.month) {
            2 -> if (period.year % 4 == 0 && (period.year % 100 != 0 || period.year % 400 == 0)) "29" else "28"
            4, 6, 9, 11 -> "30"
            else -> "31"
        }
}

@Serializable
private data class AnalyticsRequestDto(
    val startDate: String,
    val endDate: String,
    val companyIds: List<String>,
)

@Serializable
private data class AnalyticsResponseDto(
    val companies: Map<String, AnalyticsDto> = emptyMap(),
)

@Serializable
private data class AnalyticsDto(
    val votesByDay: List<VotesByDayDto> = emptyList(),
)

@Serializable
private data class VotesByDayDto(
    val data: String,
    @SerialName("Ótimo") val excellent: Int = 0,
    @SerialName("Bom") val good: Int = 0,
    @SerialName("Regular") val regular: Int = 0,
    @SerialName("Ruim") val bad: Int = 0,
    val total: Int = 0,
) {
    fun toDomain(company: Company): CustomerSatisfaction {
        val totalClients = company.employeeCount
        val participation = if (totalClients == 0) 0.0 else total.toDouble() / totalClients * 100
        return CustomerSatisfaction(
            id = "${company.id}-$data",
            date = data,
            managerName = company.managerNames.joinToString(", ").ifBlank { "-" },
            location = company.name,
            meals = totalClients,
            businessDays = 1,
            totalClients = totalClients,
            excellent = excellent,
            good = good,
            regular = regular,
            bad = bad,
            totalVotes = total,
            goalPercent = GOAL_PERCENT,
            participationPercent = participation,
            achieved = participation >= GOAL_PERCENT,
        )
    }
}
